/**
 * messageRenderer.js - Markdown, Entity Chip, and Tool Step rendering for A2UIChat
 *
 * Responsibilities:
 * 1. Render Markdown text to safe HTML (via `marked`)
 * 2. Parse entity link patterns [Label](type:UUID) into entity badge HTML
 * 3. Parse legacy tool-call text into structured tool step objects
 * 4. Render tool steps as collapsible <details> elements
 *
 * @module messageRenderer
 */

import { marked } from 'marked';

// ============================================================================
// Marked Configuration
// ============================================================================

// Configure marked for safe, non-pedantic rendering
// Note: headerIds and mangle were removed in marked v9+
marked.setOptions({
    breaks: true,       // GitHub-style line breaks
    gfm: true,          // GitHub Flavored Markdown
});

// Custom renderer: intercept entity-protocol links at the token level.
// This ensures [Label](type:UUID) → entity badge even in marked v9+/v14
// which may block or transform non-http/https protocols.
const _entityLinkRenderer = {
    link({ href, title, tokens }) {
        const ENTITY_PROTO = /^(node|resource|taxonomy_resource|ontology_type|edge|concept|person|organization|location|event):/;
        if (href && ENTITY_PROTO.test(href)) {
            const colonIdx = href.indexOf(':');
            const entityType = href.substring(0, colonIdx);
            const entityId   = href.substring(colonIdx + 1);
            const label      = (tokens && tokens[0] && tokens[0].text) ? tokens[0].text : (title || entityId);
            const cssType    = entityType === 'resource' ? 'taxonomy_resource' : entityType;
            // escapeAttr / escapeHtmlLight are defined later in this module;
            // they are in scope when this function is called (after module init).
            return `<span class="entity-badge entity-type-${escapeAttr(cssType)}"`
                + ` data-entity-id="${escapeAttr(entityId)}"`
                + ` data-entity-type="${escapeAttr(entityType)}"`
                + ` title="${escapeAttr(entityType)}: ${escapeAttr(entityId)}">`
                + `<span class="entity-label">${escapeHtmlLight(label)}</span></span>`;
        }
        // Return false to fall back to the default link renderer
        return false;
    },
};
marked.use({ renderer: _entityLinkRenderer });

// ============================================================================
// Entity Chip Rendering
// ============================================================================

/**
 * Known entity types and their CSS class suffixes.
 * Maps the type portion of [Label](type:UUID) links.
 */
const ENTITY_TYPES = new Set([
    'node',
    'resource',
    'taxonomy_resource',
    'ontology_type',
    'edge',
    'concept',
    'person',
    'organization',
    'location',
    'event',
]);

/**
 * Regex to match entity link patterns in rendered HTML.
 * After marked renders [Label](type:UUID) as <a href="type:UUID">Label</a>,
 * we post-process these anchors into entity badges.
 *
 * Permissive: allows extra attributes (rel, target, etc.) that marked v9+ may add.
 */
const ENTITY_LINK_REGEX = /<a\s[^>]*href="((?:node|resource|taxonomy_resource|ontology_type|edge|concept|person|organization|location|event):([^"]+))"[^>]*>([^<]+)<\/a>/gi;

/**
 * Raw markdown entity pattern for fallback (when text was not processed by marked).
 * Matches: [Label](type:UUID)
 */
const RAW_ENTITY_REGEX = /\[([^\]]+)\]\((node|resource|taxonomy_resource|ontology_type|edge|concept|person|organization|location|event):([^)]+)\)/g;

/**
 * Replace entity link anchors with styled entity badge spans.
 *
 * @param {string} html - HTML string (post-markdown rendering)
 * @returns {string} HTML with entity badges
 */
function replaceEntityLinks(html) {
    return html.replace(ENTITY_LINK_REGEX, (_match, _href, uuid, label) => {
        // Extract the type from the href (everything before the colon)
        const colonIdx = _href.indexOf(':');
        const entityType = _href.substring(0, colonIdx);
        const entityId = uuid;
        // Map 'resource' to 'taxonomy_resource' for CSS class consistency
        const cssType = entityType === 'resource' ? 'taxonomy_resource' : entityType;
        return `<span class="entity-badge entity-type-${escapeAttr(cssType)}" data-entity-id="${escapeAttr(entityId)}" data-entity-type="${escapeAttr(entityType)}" title="${escapeAttr(entityType)}: ${escapeAttr(entityId)}">`
            + `<span class="entity-label">${escapeHtmlLight(label)}</span></span>`;
    });
}

/**
 * Replace raw markdown entity patterns with entity badge spans.
 * Used when text was not processed through marked (e.g. plain text fallback).
 *
 * @param {string} text - Plain text or partially processed text
 * @returns {string} Text with entity badges
 */
function replaceRawEntityLinks(text) {
    return text.replace(RAW_ENTITY_REGEX, (_match, label, entityType, entityId) => {
        const cssType = entityType === 'resource' ? 'taxonomy_resource' : entityType;
        return `<span class="entity-badge entity-type-${escapeAttr(cssType)}" data-entity-id="${escapeAttr(entityId)}" data-entity-type="${escapeAttr(entityType)}" title="${escapeAttr(entityType)}: ${escapeAttr(entityId)}">`
            + `<span class="entity-label">${escapeHtmlLight(label)}</span></span>`;
    });
}

// ============================================================================
// Markdown Rendering Pipeline
// ============================================================================

/**
 * Render a text string as rich HTML with markdown + entity chips.
 *
 * Pipeline:
 *   1. marked() converts markdown to HTML
 *   2. replaceEntityLinks() converts entity <a> tags to badges
 *
 * @param {string} text - Raw markdown text from agent
 * @returns {string} Rendered HTML
 */
export function renderMarkdown(text) {
    if (!text) return '';

    // Step 1: Render markdown
    let html = marked.parse(text);

    // Step 2: Replace entity link anchors produced by marked
    html = replaceEntityLinks(html);

    // Step 3: Fallback – replace any raw [Label](type:UUID) patterns that
    // marked v9+ did not convert to anchor tags (e.g. when it strips or
    // rewrites custom-protocol URLs like resource: / node: / etc.).
    html = replaceRawEntityLinks(html);

    return html;
}

/**
 * Render plain text with entity chip support only (no markdown).
 * Used for user messages where markdown rendering may not be desired.
 *
 * @param {string} text - Plain text
 * @returns {string} HTML with entity chips
 */
export function renderTextWithEntities(text) {
    if (!text) return '';
    let html = escapeHtml(text);
    html = replaceRawEntityLinks(html);
    return html;
}

// ============================================================================
// Tool Step Parsing & Rendering
// ============================================================================

/**
 * Regex patterns for detecting legacy tool call/response text in concatenated messages.
 */
const TOOL_CALL_PATTERN = /Calling tool:\s*(\S+)\s+with args:\s*([\s\S]*?)(?=(?:Calling tool:|Tool response:|$))/g;
const TOOL_RESPONSE_PATTERN = /Tool response:\s*(\S+)\s+with result:\s*([\s\S]*?)(?=(?:Calling tool:|Tool response:|$))/g;
const TRANSFER_PATTERN = /Transferring to agent:\s*(\S+)/g;

/**
 * Parse a concatenated text blob into structured segments:
 * tool_call, tool_response, transfer, and final text.
 *
 * @param {string} text - Concatenated message text (legacy format)
 * @returns {Array<{kind: string, text: string, tool_name?: string, detail?: string}>}
 */
export function parseToolSteps(text) {
    if (!text) return [{ kind: 'text', text: '' }];

    const segments = [];
    let lastIndex = 0;
    const combined = [];

    // Find all tool-related patterns with their positions
    const toolCallRegex = /Calling tool:\s*(\S+)\s+with args:\s*([\s\S]*?)(?=Calling tool:|Tool response:|Transferring to agent:|$)/g;
    const toolResponseRegex = /Tool response:\s*(\S+)\s+with result:\s*([\s\S]*?)(?=Calling tool:|Tool response:|Transferring to agent:|$)/g;
    const transferRegex = /Transferring to agent:\s*(\S+)/g;

    let match;

    // Collect all matches with their positions
    while ((match = toolCallRegex.exec(text)) !== null) {
        combined.push({ index: match.index, end: match.index + match[0].length, kind: 'tool_call', name: match[1], detail: match[2].trim() });
    }
    while ((match = toolResponseRegex.exec(text)) !== null) {
        combined.push({ index: match.index, end: match.index + match[0].length, kind: 'tool_response', name: match[1], detail: match[2].trim() });
    }
    while ((match = transferRegex.exec(text)) !== null) {
        combined.push({ index: match.index, end: match.index + match[0].length, kind: 'transfer', name: match[1], detail: '' });
    }

    // Sort by position
    combined.sort((a, b) => a.index - b.index);

    if (combined.length === 0) {
        // No tool patterns found — return as-is
        return [{ kind: 'text', text }];
    }

    // Build segments
    for (const item of combined) {
        // Any text before this pattern is regular text
        if (item.index > lastIndex) {
            const beforeText = text.substring(lastIndex, item.index).trim();
            if (beforeText) {
                segments.push({ kind: 'text', text: beforeText });
            }
        }
        segments.push({
            kind: item.kind,
            text: item.kind === 'transfer' ? `Transferring to ${item.name}` : '',
            tool_name: item.name,
            detail: item.detail,
        });
        lastIndex = item.end;
    }

    // Any remaining text after the last pattern is the final answer
    if (lastIndex < text.length) {
        const remaining = text.substring(lastIndex).trim();
        if (remaining) {
            segments.push({ kind: 'text', text: remaining });
        }
    }

    return segments;
}

/**
 * Render an array of content parts (from backend or parsed) into HTML.
 * Handles text (markdown + entities), tool_call, and tool_response parts.
 *
 * @param {Array} parts - Content parts array
 * @param {object} options - Rendering options
 * @param {boolean} options.isUser - Whether this is a user message
 * @returns {string} Rendered HTML
 */
export function renderContentParts(parts, { isUser = false } = {}) {
    if (!Array.isArray(parts) || parts.length === 0) return '';

    let html = '';
    let toolStepsHtml = '';
    let inToolGroup = false;

    for (const part of parts) {
        const kind = part.type || part.kind || 'text';

        if (kind === 'tool_call' || kind === 'tool_response') {
            // Hide ALL tool parts — the user only sees the final text response
            continue;
        } else if (kind === 'transfer') {
            if (!inToolGroup) {
                toolStepsHtml = '<div class="a2ui-tool-steps">';
                inToolGroup = true;
            }
            toolStepsHtml += renderTransferStep(part);
        } else if (kind === 'text') {
            // Close any open tool group before text
            if (inToolGroup) {
                toolStepsHtml += '</div>';
                html += toolStepsHtml;
                toolStepsHtml = '';
                inToolGroup = false;
            }
            const text = part.text || '';
            if (text) {
                if (isUser) {
                    html += `<div class="a2ui-message-text">${renderTextWithEntities(text)}</div>`;
                } else {
                    html += `<div class="a2ui-message-text a2ui-markdown">${renderMarkdown(text)}</div>`;
                }
            }
        }
        // Other types (mcp_ui, a2ui, image, file) are handled by the caller
    }

    // Close trailing tool group
    if (inToolGroup) {
        toolStepsHtml += '</div>';
        html += toolStepsHtml;
    }

    return html;
}

/**
 * Render a single tool call or tool response as a collapsible <details> element.
 *
 * @param {object} part - Tool step part
 * @returns {string} HTML
 */
function renderToolStep(part) {
    const kind = part.type || part.kind;
    const toolName = part.tool_name || '';
    const isCall = kind === 'tool_call';
    const icon = isCall ? 'wrench' : 'check-circle';
    const label = isCall
        ? `Tool: ${escapeHtmlLight(toolName)}`
        : `Result: ${escapeHtmlLight(toolName)}`;

    // Build detail content
    let detailContent = '';
    if (isCall) {
        const args = part.tool_args || part.detail || '';
        detailContent = typeof args === 'object' ? JSON.stringify(args, null, 2) : String(args);
    } else {
        const result = part.tool_result || part.detail || '';
        detailContent = typeof result === 'object' ? JSON.stringify(result, null, 2) : String(result);
    }

    // Truncate very large outputs
    const MAX_DETAIL_LENGTH = 2000;
    let truncated = false;
    if (detailContent.length > MAX_DETAIL_LENGTH) {
        detailContent = detailContent.substring(0, MAX_DETAIL_LENGTH);
        truncated = true;
    }

    return `<details class="a2ui-tool-step ${isCall ? 'tool-call' : 'tool-response'}">
        <summary><i data-lucide="${icon}"></i> <span>${label}</span></summary>
        <pre class="a2ui-tool-detail">${escapeHtml(detailContent)}${truncated ? '\n... (truncated)' : ''}</pre>
    </details>`;
}

/**
 * Render a transfer step.
 *
 * @param {object} part - Transfer part
 * @returns {string} HTML
 */
function renderTransferStep(part) {
    const agentName = part.tool_name || part.text || 'unknown';
    return `<div class="a2ui-tool-step transfer">
        <i data-lucide="arrow-right-circle"></i>
        <span>Transferring to ${escapeHtmlLight(agentName)}</span>
    </div>`;
}

// ============================================================================
// HTML Escaping Utilities
// ============================================================================

/**
 * Full HTML escape - for use in text content.
 */
export function escapeHtml(str) {
    if (!str) return '';
    return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
}

/**
 * Light HTML escape — for content already partially processed.
 * Does not escape quotes (for inline use within already-safe contexts).
 */
function escapeHtmlLight(str) {
    if (!str) return '';
    return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
}

/**
 * Escape for use in HTML attributes.
 */
function escapeAttr(str) {
    if (!str) return '';
    return String(str)
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
}
